import React, { useMemo, useRef, useEffect } from 'react';
import RecapSection from '../RecapSection';
import UmlautHelper from './UmlautHelper';
import ReportSoalButton from '../../../LanguageLearning/ReportSoalButton';
import { ReviewQuestionHeader } from '../shared/ReviewQuestionHeader';

// Get base URL for audio
const getBaseURL = () => {
  return process.env.REACT_APP_API_URL || 'http://localhost:3000';
};

const EssayReview = ({ question, userAnswer, isCorrect, feedback, explanation, recap = '', annotations = [], langCode = null, reportProps = null }) => {
  // Get readingText from multiple sources and keep the largest one
  const questionPassage = question.passage || '';
  const questionReadingText = question['reading-text'] || '';
  const questionContext = question.context || '';
  let storagePassage = '';
  
  // Try to get passage from localStorage
  try {
    const qpSessionRaw = localStorage.getItem("qp-session");
    if (qpSessionRaw) {
      const parsed = JSON.parse(qpSessionRaw);
      storagePassage = parsed.passage || '';
    } else {
      const qpPracticeRaw = localStorage.getItem("qp-practice");
      if (qpPracticeRaw) {
        const parsed = JSON.parse(qpPracticeRaw);
        storagePassage = parsed.passage || '';
      }
    }
  } catch (error) {
    // Ignore localStorage errors
  }
  
  // Keep the largest readingText from all sources
  const allSources = [questionPassage, questionReadingText, questionContext, storagePassage];
  const readingText = allSources.reduce((largest, current) => 
    (current && current.length > (largest || '').length) ? current : largest, '');
  
  const prompt = question.text || question['text-soal'] || question.question || question.prompt || '';
  const audioRef = useRef(null);
  const activeAnnotationRef = useRef(null);
  
  // Normalize user answer - ensure it's always a string
  let essayText = '';
  if (userAnswer !== null && userAnswer !== undefined) {
    if (typeof userAnswer === 'string') {
      essayText = userAnswer;
    } else {
      essayText = String(userAnswer);
    }
  }
  
  const wordCount = essayText.trim ? essayText.trim().split(/\s+/).filter(word => word.length > 0).length : 0;

  // Build annotations map from question.annotations array
  const annotationsMap = useMemo(() => {
    const map = {};
    
    // First, try to get annotations from question.annotations array
    if (question.annotations && Array.isArray(question.annotations)) {
      question.annotations.forEach((annotation) => {
        const annId = annotation['ann-id'] || annotation.annId || annotation.id;
        const streamUrl = annotation['stream-url'] || annotation.streamUrl || annotation.stream_url;
        const annType = annotation['ann-type'] || annotation.annType || annotation.type;
        
        if (annId && streamUrl && annType === 'audio') {
          map[annId] = {
            annId,
            streamUrl,
            annType,
          };
        }
      });
    }
    
    // Also parse HTML for data-ann-id attributes (fallback)
    const htmlToParse = readingText || prompt || '';
    if (htmlToParse && typeof window !== 'undefined' && typeof window.DOMParser !== 'undefined') {
      try {
        const parser = new window.DOMParser();
        const doc = parser.parseFromString(htmlToParse, 'text/html');
        const nodes = doc.querySelectorAll('[data-ann-id]');

        nodes.forEach((node) => {
          const annId = node.getAttribute('data-ann-id');
          const annPath = node.getAttribute('data-ann-path');
          const annType = node.getAttribute('data-ann-type');

          // Only add if not already in map (from annotations array)
          if (annId && annPath && annType === 'audio' && !map[annId]) {
            // Convert data-ann-path to streamUrl
            const baseURL = getBaseURL();
            const streamUrl = `${baseURL}/${annPath}`;
            
            map[annId] = {
              annId,
              streamUrl,
              annType,
            };
          }
        });
      } catch (error) {
        console.warn('EssayReview: failed to parse annotations from HTML', error);
      }
    }

    return map;
  }, [question.annotations, readingText, prompt]);

  // Enhance HTML for reading text with annotation support
  const enhancedReadingText = useMemo(() => {
    if (!readingText || typeof window === 'undefined' || typeof window.DOMParser === 'undefined') {
      return readingText;
    }

    try {
      const parser = new window.DOMParser();
      const doc = parser.parseFromString(readingText, 'text/html');
      const nodes = doc.querySelectorAll('[data-ann-id]');

      nodes.forEach((node) => {
        const annId = node.getAttribute('data-ann-id');
        if (annId && annotationsMap[annId]) {
          if (!node.classList.contains('practice-annotation')) {
            node.classList.add('practice-annotation');
          }
          if (!node.classList.contains('annotation-hidden')) {
            node.classList.add('annotation-hidden');
          }
          if (!node.classList.contains('annotation')) {
            node.classList.add('annotation');
          }
          
          if (!node.hasAttribute('tabindex')) {
            node.setAttribute('tabindex', '0');
          }
          
          if (!node.hasAttribute('role')) {
            node.setAttribute('role', 'button');
          }
          
          if (!node.hasAttribute('aria-label')) {
            node.setAttribute('aria-label', `Play audio`);
          }
        }
      });

      return doc.body.innerHTML;
    } catch (error) {
      console.warn('EssayReview: failed to enhance reading text HTML', error);
      return readingText;
    }
  }, [readingText, annotationsMap]);

  // Enhance HTML with annotation classes for prompt
  const enhancedPrompt = useMemo(() => {
    if (!prompt || typeof window === 'undefined' || typeof window.DOMParser === 'undefined') {
      return prompt;
    }

    try {
      const parser = new window.DOMParser();
      const doc = parser.parseFromString(prompt, 'text/html');
      const nodes = doc.querySelectorAll('[data-ann-id]');

      nodes.forEach((node) => {
        const annId = node.getAttribute('data-ann-id');
        if (annId && annotationsMap[annId]) {
          if (!node.classList.contains('practice-annotation')) {
            node.classList.add('practice-annotation');
          }
          if (!node.classList.contains('annotation-hidden')) {
            node.classList.add('annotation-hidden');
          }
          if (!node.classList.contains('annotation')) {
            node.classList.add('annotation');
          }
          
          if (!node.hasAttribute('tabindex')) {
            node.setAttribute('tabindex', '0');
          }
          
          if (!node.hasAttribute('role')) {
            node.setAttribute('role', 'button');
          }
          
          if (!node.hasAttribute('aria-label')) {
            node.setAttribute('aria-label', `Play audio`);
          }
        }
      });

      return doc.body.innerHTML;
    } catch (error) {
      console.warn('EssayReview: failed to enhance prompt HTML', error);
      return prompt;
    }
  }, [prompt, annotationsMap]);

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  const highlightAnnotation = (element) => {
    if (!element) return;

    if (activeAnnotationRef.current && activeAnnotationRef.current !== element) {
      activeAnnotationRef.current.classList.remove('practice-annotation-active');
    }

    activeAnnotationRef.current = element;
    element.classList.add('practice-annotation-active');
  };

  const playAnnotationAudio = (annotation) => {
    const streamUrl = annotation?.streamUrl;
    
    if (!streamUrl) {
      console.warn('EssayReview: No streamUrl found in annotation:', annotation);
      return;
    }

    if (!audioRef.current) {
      audioRef.current = new Audio();
      audioRef.current.addEventListener('ended', () => {
        if (activeAnnotationRef.current) {
          activeAnnotationRef.current.classList.remove('practice-annotation-active');
          activeAnnotationRef.current = null;
        }
      });
      audioRef.current.addEventListener('error', (e) => {
        console.error('EssayReview: Audio playback error:', e);
      });
    }

    const audioEl = audioRef.current;
    audioEl.pause();
    audioEl.src = streamUrl;
    audioEl.currentTime = 0;
    audioEl
      .play()
      .catch((error) => {
        console.error('EssayReview: failed to play annotation audio', error);
      });
  };

  const findAnnotationTrigger = (event) => {
    const target = event.target;
    if (target && typeof target.closest === 'function') {
      return target.closest('[data-ann-id]');
    }
    return null;
  };

  const handleAnnotationTrigger = (event) => {
    if (!annotationsMap || Object.keys(annotationsMap).length === 0) {
      return;
    }

    const trigger = findAnnotationTrigger(event);
    if (!trigger) {
      return;
    }

    const annId = trigger.getAttribute('data-ann-id');
    if (!annId || !annotationsMap[annId]) {
      return;
    }

    const annotation = annotationsMap[annId];
    highlightAnnotation(trigger);
    playAnnotationAudio(annotation);
  };

  const handleAnnotationKeyDown = (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    event.preventDefault();
    handleAnnotationTrigger(event);
  };

  const hasAnnotations = annotationsMap && Object.keys(annotationsMap).length > 0;

  return (
    <div className="bg-[var(--card-bg)] rounded-xl p-6 mb-6 max-xl:px-0 max-xl:py-2">
      <ReviewQuestionHeader
        questionType="Essay Writing"
        reportSlot={reportProps && (
          <ReportSoalButton
            type={reportProps.reportType}
            prosetId={reportProps.prosetId}
            soalId={reportProps.soalId}
            allowReReport={true}
            persistReportedState={true}
            reportText="Report this question"
          />
        )}
        currentQuestionNumber={reportProps?.currentQuestionNumber}
        totalQuestions={reportProps?.totalQuestions}
        score={reportProps?.score}
      />

      <div className="flex flex-col gap-5">
        {readingText && (
          <div className="mb-3 p-4 rounded-lg border-l-4 border-blue-500 bg-blue-50 dark:bg-blue-500/10 max-xl:px-4 max-xl:py-3">
            <h4 className="text-base font-bold mb-3 text-blue-600 dark:text-blue-400 flex items-center">Reading Text:</h4>
            <div
              className="text-[15px] leading-[1.7] text-[var(--text-primary)]"
              onClick={hasAnnotations ? handleAnnotationTrigger : undefined}
              onKeyDown={hasAnnotations ? handleAnnotationKeyDown : undefined}
              dangerouslySetInnerHTML={{ __html: enhancedReadingText }}
            />
          </div>
        )}

        <div className="text-base leading-relaxed text-[var(--text-primary)]">
          {prompt && (
            <div
              onClick={hasAnnotations ? handleAnnotationTrigger : undefined}
              onKeyDown={hasAnnotations ? handleAnnotationKeyDown : undefined}
              dangerouslySetInnerHTML={{ __html: enhancedPrompt }}
            />
          )}
        </div>

        <div className="p-4 rounded-lg mt-4 mb-0">
          <h4 className="text-sm font-bold mb-3 text-[var(--text-primary)] uppercase tracking-wide flex items-center gap-2">
            Your Essay:
            <UmlautHelper showTooltip={false} langCode={langCode} />
          </h4>
          <div className="mt-3 mb-0">
            {essayText ? (
              <div className="text-[15px] leading-[1.7] text-[var(--text-primary)] whitespace-pre-wrap break-words">
                <p className="m-0">{essayText}</p>
              </div>
            ) : (
              <p className="text-[var(--text-tertiary)] italic text-sm m-0">No essay provided</p>
            )}
          </div>
        </div>

        {feedback && (
          <div className="mt-5 p-5 rounded-lg border-l-4 border-amber-500 bg-amber-50 dark:bg-amber-500/10">
            <h4 className="text-sm font-bold uppercase tracking-wide text-orange-600 dark:text-amber-400 mb-2">💡 Feedback:</h4>
            <div className="text-[15px] leading-[1.7] text-gray-800 dark:text-slate-200 [&_p]:m-0">
              <p>{feedback}</p>
            </div>
          </div>
        )}

        {explanation && (
          <div className={`bg-[linear-gradient(135deg,#fff9e6_0%,#ffeaa7_100%)] dark:bg-[linear-gradient(135deg,#3d3520_0%,#4a3d1a_100%)] p-5 rounded-xl mb-6 border-l-4 border-l-[#ffd93d] ${feedback ? 'mt-1' : ''}`}>
            <h3 className="text-base font-bold text-amber-600 dark:text-amber-400 mb-3 flex items-center gap-2">Penjelasan</h3>
            <p className="text-[15px] text-[var(--text-primary)] leading-relaxed m-0">{explanation}</p>
          </div>
        )}

        <RecapSection recap={recap} annotations={annotations} question={question} />
      </div>
    </div>
  );
};

export default EssayReview;

